import { useEffect, useRef, useState, useCallback } from 'react';
import sessionService from '@/services/sessionService.ts';
import type { SessionDetailResponse } from '@/types/session.ts';

const POLL_INTERVAL_MS = 5000;
const TERMINAL = new Set(['Complete', 'Failed']);

export type ReportStatus = 'Pending' | 'Processing' | 'Complete' | 'Failed' | string;

interface UseReportGenerationPollerResult {
  status: ReportStatus | null;
  session: SessionDetailResponse | null;
  setOptimisticStatus: (next: ReportStatus | null) => void;
}

export function useReportGenerationPoller(
  sessionId: string | null,
  initialSession: SessionDetailResponse | null,
): UseReportGenerationPollerResult {
  const [session, setSession] = useState<SessionDetailResponse | null>(initialSession);
  const [status, setStatus] = useState<ReportStatus | null>(
    initialSession?.report?.aiAnalysisStatus ?? null,
  );
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setSession(initialSession);
    setStatus(initialSession?.report?.aiAnalysisStatus ?? null);
  }, [initialSession]);

  const tick = useCallback(async () => {
    if (!sessionId) return;
    try {
      const detail = await sessionService.getById(sessionId);
      setSession(detail);
      setStatus(detail.report?.aiAnalysisStatus ?? 'Pending');
    } catch {
      // Keep polling on transient errors; the next tick retries.
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const start = () => {
      stop();
      tick();
      intervalRef.current = setInterval(tick, POLL_INTERVAL_MS);
    };

    if (status && TERMINAL.has(status)) {
      stop();
      return;
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    document.addEventListener('visibilitychange', handleVisibility);

    if (document.visibilityState === 'visible') start();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stop();
    };
  }, [sessionId, status, tick]);

  return { status, session, setOptimisticStatus: setStatus };
}
